import { useState, useCallback } from "react";
import { message } from "antd";
import { createOperation, updateOperation, deleteOperation } from "../services/operationService";

export const usePermissionManagement = () => {
  const [loading, setLoading] = useState(false);
  const [createModalVisible, setCreateModalVisible] = useState(false);
  const [editModalVisible, setEditModalVisible] = useState(false);
  const [currentRecord, setCurrentRecord] = useState<any>(null);

  // 操作名称中文映射
  const actionNameMap: Record<string, string> = {
    view: "查看",
    create: "创建",
    update: "更新",
    delete: "删除",
    export: "导出",
    import: "导入",
    "*": "全部操作",
    all: "全部操作",
  };

  // 需要自动生成的操作列表
  const autoGenerateActions = ["view", "create", "update", "delete", "export", "import", "*", "all"];

  // 创建权限（自动生成多个权限）
  const handleCreate = useCallback(async (values: any): Promise<boolean> => {
    try {
      setLoading(true);

      const { serviceName, page } = values;

      // 获取微服务名称（用于命名）
      const serviceLabel = serviceName; // 使用微服务名称（英文）

      // 批量创建权限
      const createPromises = autoGenerateActions.map(async (action) => {
        // 构建权限名称：微服务+页面名称（英文）+操作名称（中文）
        const actionName = actionNameMap[action] || action;
        const permissionName = `${serviceLabel}${page}${actionName}`;

        // 构建权限代码
        const code = `${serviceName}:${page}:${action}`;

        // 构建权限数据
        const permissionData = {
          name: permissionName,
          code: code,
          description: "", // 不需要描述
          serviceName: serviceName,
          page: page,
          action: action,
          isActive: true, // 所有状态为可用
          isSystem: false,
        };

        return createOperation(permissionData);
      });

      // 等待所有权限创建完成
      const results = await Promise.allSettled(createPromises);

      // 检查结果
      const successCount = results.filter((r) => r.status === "fulfilled" && r.value?.success).length;
      const failCount = results.length - successCount;

      if (successCount > 0) {
        if (failCount === 0) {
          message.success(`成功创建 ${successCount} 个权限`);
        } else {
          message.warning(`成功创建 ${successCount} 个权限，${failCount} 个失败`);
        }
        setCreateModalVisible(false);
        return true;
      } else {
        message.error("创建权限失败");
        return false;
      }
    } catch (error) {
      console.error("创建权限失败:", error);
      message.error("创建权限失败");
      return false;
    } finally {
      setLoading(false);
    }
  }, []);

  // 编辑权限
  const handleEdit = useCallback(
    async (values: any): Promise<boolean> => {
      try {
        setLoading(true);

        // 构建权限代码
        const code = `${values.serviceName}:${values.page}:${values.action}`;

        // 构建权限数据
        const permissionData = {
          name: values.name,
          code: code,
          description: values.description || "",
          serviceName: values.serviceName, // 改为 serviceName
          page: values.page,
          action: values.action,
          isActive: values.isActive === "active" || values.isActive === true, // 改为 isActive
          isSystem: false, // 改为 isSystem
        };

        console.log("编辑权限发送的数据:", permissionData);
        console.log("权限ID:", currentRecord?.id);

        const response = await updateOperation(currentRecord?.id, permissionData);
        if (response.success) {
          message.success("权限更新成功");
          setEditModalVisible(false);
          setCurrentRecord(null);
          return true;
        } else {
          message.error(response.message || "更新权限失败");
          return false;
        }
      } catch (error) {
        console.error("更新权限失败:", error);
        message.error("更新权限失败");
        return false;
      } finally {
        setLoading(false);
      }
    },
    [currentRecord]
  );

  // 删除权限
  const handleDelete = useCallback(async (id: string): Promise<boolean> => {
    try {
      setLoading(true);
      await deleteOperation(id);
      message.success("权限删除成功");
      return true;
    } catch (error) {
      console.error("删除权限失败:", error);
      // 尝试从错误对象中提取错误信息
      let errorMessage = "删除权限失败";
      if (error instanceof Error) {
        // 如果错误消息不是默认格式，使用它
        if (error.message && !error.message.startsWith("删除操作失败")) {
          errorMessage = error.message;
        } else if (error.message) {
          // 如果是默认格式，尝试提取更具体的错误信息
          errorMessage = error.message;
        }
      } else if (error && typeof error === "object" && "message" in error) {
        // 如果是包含 message 属性的对象
        errorMessage = String((error as any).message) || errorMessage;
      }
      message.error(errorMessage);
      return false;
    } finally {
      setLoading(false);
    }
  }, []);

  // 打开创建模态框
  const openCreateModal = useCallback(() => {
    setCreateModalVisible(true);
  }, []);

  // 关闭创建模态框
  const closeCreateModal = useCallback(() => {
    setCreateModalVisible(false);
  }, []);

  // 打开编辑模态框
  const openEditModal = useCallback((record: any) => {
    setCurrentRecord(record);
    setEditModalVisible(true);
  }, []);

  // 关闭编辑模态框
  const closeEditModal = useCallback(() => {
    setEditModalVisible(false);
    setCurrentRecord(null);
  }, []);

  return {
    // 状态
    loading,
    createModalVisible,
    editModalVisible,
    currentRecord,

    // 操作方法
    handleCreate,
    handleEdit,
    handleDelete,

    // 模态框控制
    openCreateModal,
    closeCreateModal,
    openEditModal,
    closeEditModal,
  };
};
